import { useState } from "react";
import { Shield, Globe } from "lucide-react";
import type { PolicyData, PolicySection, PolicyBlock } from "./content";
import { en, it } from "./content";

type Lang = "en" | "it";

const DATA: Record<Lang, PolicyData> = { en, it };

function mapLocaleToLang(locale: string | undefined): Lang {
  if (locale?.startsWith("it")) return "it";
  return "en";
}

/* ── Block renderers ───────────────────────────── */

function BlockRenderer({ block }: { block: PolicyBlock }) {
  switch (block.kind) {
    case "p":
      return (
        <p className="text-sm leading-relaxed text-text-secondary sm:text-base">
          {block.text}
        </p>
      );
    case "ul":
      return (
        <ul className="space-y-2 pl-5">
          {block.items.map((item, i) => (
            <li
              key={i}
              className="list-disc text-sm leading-relaxed text-text-secondary marker:text-primary sm:text-base"
            >
              {item}
            </li>
          ))}
        </ul>
      );
    case "table":
      return (
        <div className="overflow-x-auto rounded-card border border-border">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-raised">
                {block.cols.map((col, i) => (
                  <th
                    key={i}
                    className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-primary sm:text-sm"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr
                  key={ri}
                  className="border-b border-border last:border-b-0 even:bg-surface/50"
                >
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      className="px-4 py-3 text-sm leading-relaxed text-text-secondary"
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

/* ── Section renderer ──────────────────────────── */

function SectionRenderer({
  section,
  isSubsection = false,
}: {
  section: PolicySection;
  isSubsection?: boolean;
}) {
  return (
    <section className={isSubsection ? "space-y-4" : "space-y-5"}>
      <h3
        className={`font-display font-bold text-text ${
          isSubsection ? "text-base sm:text-lg" : "text-lg sm:text-xl"
        }`}
      >
        <span className="text-primary">{section.number}.</span> {section.title}
      </h3>

      {section.blocks.map((block, i) => (
        <BlockRenderer key={i} block={block} />
      ))}

      {section.subsections && (
        <div className="space-y-8 pl-0 sm:pl-4">
          {section.subsections.map((sub) => (
            <SectionRenderer key={sub.number} section={sub} isSubsection />
          ))}
        </div>
      )}
    </section>
  );
}

/* ── Page component ────────────────────────────── */

export function PolicyPage({ initialLang }: { initialLang?: string }) {
  const [lang, setLang] = useState<Lang>(mapLocaleToLang(initialLang));
  const d = DATA[lang];

  return (
    <div className="h-dvh overflow-y-auto bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-surface/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="rounded-button bg-primary-light p-2">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <span className="font-display text-lg font-bold text-text sm:text-xl">
              {d.title}
            </span>
          </div>

          {/* Language toggle */}
          <div className="flex items-center gap-1 rounded-button border border-border bg-surface p-1">
            <button
              type="button"
              onClick={() => setLang("en")}
              className={`flex items-center gap-1.5 rounded-input px-3 py-1.5 text-xs font-medium transition sm:text-sm ${
                lang === "en"
                  ? "bg-primary text-text-inverse"
                  : "text-text-secondary hover:text-text"
              }`}
            >
              <Globe className="h-3.5 w-3.5" />
              EN
            </button>
            <button
              type="button"
              onClick={() => setLang("it")}
              className={`flex items-center gap-1.5 rounded-input px-3 py-1.5 text-xs font-medium transition sm:text-sm ${
                lang === "it"
                  ? "bg-primary text-text-inverse"
                  : "text-text-secondary hover:text-text"
              }`}
            >
              <Globe className="h-3.5 w-3.5" />
              IT
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-12">
        <div className="space-y-12">
          {/* Title block */}
          <div className="text-center">
            <h1 className="font-display text-2xl font-bold text-text sm:text-3xl lg:text-4xl">
              {d.title}
            </h1>
            <p className="mt-2 text-sm text-text-muted sm:text-base">
              {d.subtitle}
            </p>
          </div>

          {/* Preamble */}
          <div className="space-y-4 rounded-card border border-border bg-surface/40 p-6 sm:p-8">
            <h2 className="font-display text-lg font-bold text-text sm:text-xl">
              {d.preambleTitle}
            </h2>
            {d.preamble.map((p, i) => (
              <p
                key={i}
                className="text-sm leading-relaxed text-text-secondary sm:text-base"
              >
                {p}
              </p>
            ))}
          </div>

          {/* Separator */}
          <div className="flex items-center gap-4">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-medium uppercase tracking-widest text-text-muted">
              ****
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          {/* Sections */}
          <div className="space-y-10">
            {d.sections.map((section) => (
              <SectionRenderer key={section.number} section={section} />
            ))}
          </div>

          {/* Footer */}
          <footer className="border-t border-border pt-8 text-center">
            <p className="text-xs text-text-muted sm:text-sm">
              Hivly S.r.l. &mdash; Viale Lucania 35, Milano
            </p>
            <p className="mt-1 text-xs text-text-muted">
              <a
                href="mailto:hello@thepack.tech"
                className="text-primary transition hover:text-primary-hover"
              >
                info@hivly.org
              </a>
            </p>
          </footer>
        </div>
      </main>
    </div>
  );
}
